// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';

interface LibraryItem {
  _id: string;
  title: string;
  description?: string;
  sourceDomain?: string;
  sourceType: string;
  contentType: string;
  originalUrl: string;
  canonicalUrl?: string;
  thumbnailUrl?: string;
  author?: string;
  publisher?: string;
  publishedAt?: string;
  createdAt?: string;
  extractedText?: string;
  mediaUrl?: string;
  readState: 'unread' | 'reading' | 'read';
  savedState: 'saved' | 'archived' | 'deleted';
  isFavorite?: boolean;
  tags?: string[];
  collectionIds?: string[];
  note?: string;
}

interface CollectionItem {
  _id: string;
  name: string;
}

interface ReaderDetailPaneProps {
  item: LibraryItem | null;
  collections: CollectionItem[];
  onBack?: () => void;
  onToggleRead: (item: LibraryItem) => void;
  onToggleFavorite: (item: LibraryItem) => void;
  onToggleArchive: (item: LibraryItem) => void;
  onDelete: (item: LibraryItem) => void;
  onUpdateNote: (item: LibraryItem, note: string) => void;
  onUpdateTags: (item: LibraryItem, tags: string[]) => void;
  onUpdateCollections: (item: LibraryItem, collectionIds: string[]) => void;
  onRefreshItem?: (item: LibraryItem) => void;
  onEdit?: (item: LibraryItem) => void;
  showBackButton?: boolean;
}

function calculateReadingTime(text?: string) {
  if (!text) return '1 min read';
  const clean = text.replace(/<[^>]*>/g, ' ');
  const words = clean.trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

/**
 * Cleans extracted article HTML on the client side:
 * - Prunes client-rendered social share bars, follow buttons, and breadcrumb containers.
 * - Strips empty <li> list items that render as stray bullet points.
 * - Removes empty <ul> and <ol> list containers.
 */
function cleanArticleBody(rawHtml?: string): string {
  if (!rawHtml || typeof rawHtml !== 'string') return '';

  let html = rawHtml
    // Strip social share widgets, follow buttons, and breadcrumb containers
    .replace(/<(?:div|ul|section|aside)\b[^>]*(?:class|id)=["'][^"']*(?:share-buttons|social-share|social-links|share-bar|sharing-widget|follow-author|follow-category|category-breadcrumb|popover|duet--article--share-buttons|duet--article--article-byline|duet--article--lede|sponsorship|advertisement|concert-ad|newsletter|subscription)[^"']*["'][^>]*>[\s\S]*?<\/(?:div|ul|section|aside)>/gi, '')
    // Strip lede block if it appears before entry body
    .replace(/<div\b[^>]*class=["'][^"']*(?:duet--article--lede|article-lede|entry-header|post-header)[^"']*["'][^>]*>[\s\S]*?<\/div>(?=\s*<div\b[^>]*class=["'][^"']*entry-body)/i, '');

  // Strip empty li tags (including ones with only empty divs/spans) and empty lists
  let prev = '';
  while (html !== prev) {
    prev = html;
    html = html.replace(/<li\b[^>]*>(?:\s*<(?:div|span|p|a|em|strong|b|i)[^>]*>\s*<\/(?:div|span|p|a|em|strong|b|i)>\s*|\s*)*<\/li>/gi, '');
    html = html.replace(/<li\b[^>]*>\s*<\/li>/gi, '');
    html = html.replace(/<(ul|ol)\b[^>]*>\s*<\/\1>/gi, '');
  }

  return html;
}

export default function ReaderDetailPane({
  item,
  collections = [],
  onBack,
  onToggleRead,
  onToggleFavorite,
  onToggleArchive,
  onDelete,
  onUpdateNote,
  onUpdateTags,
  onUpdateCollections,
  onRefreshItem,
  onEdit,
  showBackButton = false,
}: ReaderDetailPaneProps) {
  const [fontSerif, setFontSerif] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [newTagInput, setNewTagInput] = useState('');
  const [isNoteSaving, setIsNoteSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const itemCollectionIds = React.useMemo(() => {
    if (!item || !item.collectionIds) return [];
    return item.collectionIds.map((cid) =>
      typeof cid === 'object' && cid && cid._id ? cid._id : String(cid)
    );
  }, [item?.collectionIds]);

  const itemCollections = React.useMemo(() => {
    return collections.filter((c) => itemCollectionIds.includes(c._id));
  }, [collections, itemCollectionIds]);

  const [deleteSecondsLeft, setDeleteSecondsLeft] = useState<number | null>(null);
  const deleteTimerRef = useRef<{ timeout: any; interval: any } | null>(null);

  useEffect(() => {
    return () => {
      if (deleteTimerRef.current) {
        clearTimeout(deleteTimerRef.current.timeout);
        clearInterval(deleteTimerRef.current.interval);
      }
    };
  }, []);

  // When item changes, cancel any pending delete
  useEffect(() => {
    if (deleteTimerRef.current) {
      clearTimeout(deleteTimerRef.current.timeout);
      clearInterval(deleteTimerRef.current.interval);
      deleteTimerRef.current = null;
      setDeleteSecondsLeft(null);
    }
  }, [item?._id]);

  const handleDeleteClick = () => {
    if (deleteSecondsLeft !== null) {
      // Cancel delete
      if (deleteTimerRef.current) {
        clearTimeout(deleteTimerRef.current.timeout);
        clearInterval(deleteTimerRef.current.interval);
        deleteTimerRef.current = null;
      }
      setDeleteSecondsLeft(null);
      return;
    }

    setDeleteSecondsLeft(10);
    const interval = setInterval(() => {
      setDeleteSecondsLeft((prev) => (prev !== null && prev > 1 ? prev - 1 : prev));
    }, 1000);

    const timeout = setTimeout(() => {
      if (deleteTimerRef.current) {
        clearInterval(deleteTimerRef.current.interval);
        deleteTimerRef.current = null;
      }
      setDeleteSecondsLeft(null);
      onDelete(item);
    }, 10000);

    deleteTimerRef.current = { timeout, interval };
  };

  useEffect(() => {
    if (item) {
      setNoteText(item.note || '');
    }
  }, [item?._id]);

  if (!item) {
    return (
      <main className="reader-detail-pane" aria-label="Reading Pane">
        <div className="reader-empty-state">
          <i className="fi fi-rr-book-open-cover reader-empty-icon" />
          <div className="reader-empty-title">Select an item to read</div>
          <div className="reader-empty-subtitle">
            Choose an article, podcast, or feed item from the list to start reading in distraction-free mode.
          </div>
        </div>
      </main>
    );
  }

  const isRead = item.readState === 'read';
  const isFavorite = !!item.isFavorite;
  const isArchived = item.savedState === 'archived';

  const handleSaveNote = () => {
    setIsNoteSaving(true);
    onUpdateNote(item, noteText);
    setTimeout(() => setIsNoteSaving(false), 500);
  };

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newTagInput.trim()) {
      e.preventDefault();
      const currentTags = item.tags || [];
      const newTag = newTagInput.trim().toLowerCase();
      if (!currentTags.includes(newTag)) {
        onUpdateTags(item, [...currentTags, newTag]);
      }
      setNewTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const currentTags = item.tags || [];
    onUpdateTags(item, currentTags.filter((t) => t !== tagToRemove));
  };

  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(item.originalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <main className="reader-detail-pane" aria-label="Reading Pane">
      {/* Header Actions Toolbar */}
      <div className="reader-detail-header-actions">
        <div className="reader-detail-actions-group">
          {showBackButton && (
            <button
              className="reader-btn reader-btn-icon"
              onClick={onBack}
              title="Back to list"
              aria-label="Back to content list"
            >
              <i className="fi fi-rr-arrow-left" />
            </button>
          )}

          {/* Mark read / unread */}
          <button
            className={`reader-btn-icon ${isRead ? 'active' : ''}`}
            onClick={() => onToggleRead(item)}
            title={isRead ? 'Mark as unread' : 'Mark as read'}
          >
            <i className={`fi fi-rr-${isRead ? 'check' : 'circle'}`} />
          </button>

          {/* Star / Favorite */}
          <button
            className={`reader-btn-icon ${isFavorite ? 'active' : ''}`}
            onClick={() => onToggleFavorite(item)}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <i className={`fi fi-${isFavorite ? 'sr-star' : 'rr-star'}`} style={{ color: isFavorite ? '#eab308' : 'inherit' }} />
          </button>

          {/* Archive */}
          <button
            className={`reader-btn-icon ${isArchived ? 'active' : ''}`}
            onClick={() => onToggleArchive(item)}
            title={isArchived ? 'Unarchive' : 'Archive'}
          >
            <i className="fi fi-rr-box-alt" />
          </button>

          {/* Edit Link & Move Collections Button */}
          {onEdit && (
            <button
              className="reader-btn"
              onClick={() => onEdit(item)}
              title="Edit link details & move to collections"
              aria-label="Edit link and collections"
              style={{ fontSize: '0.8rem', gap: '6px' }}
            >
              <i className="fi fi-rr-edit" style={{ fontSize: '0.78rem' }} />
              <span>Edit</span>
            </button>
          )}

          {/* Collections Button */}
          <button
            type="button"
            className="reader-btn"
            onClick={() => onEdit && onEdit(item)}
            title={
              itemCollections.length > 0
                ? `Collections: ${itemCollections.map((c) => c.name).join(', ')}. Click to edit / move.`
                : 'Move to collections'
            }
            style={{ fontSize: '0.78rem', gap: '6px' }}
          >
            <i className="fi fi-rr-folder" style={{ fontSize: '0.75rem', color: 'var(--reader-accent)' }} />
            <span>
              {itemCollections.length === 0
                ? 'Add to Collection...'
                : itemCollections.length === 1
                ? itemCollections[0].name
                : `${itemCollections.length} Collections`}
            </span>
          </button>
        </div>

        <div className="reader-detail-actions-group">
          {/* Font Toggle (Sans vs Serif) */}
          <button
            className={`reader-btn ${fontSerif ? 'reader-btn-primary' : ''}`}
            style={{ fontSize: '0.78rem', padding: '4px 8px' }}
            onClick={() => setFontSerif(!fontSerif)}
            title="Toggle serif typography"
          >
            <span style={{ fontFamily: 'Georgia, serif', fontWeight: 600 }}>T</span>
            <span>{fontSerif ? 'Serif' : 'Sans'}</span>
          </button>

          {/* Copy link */}
          <button
            className="reader-btn-icon"
            onClick={handleCopyLink}
            title={copied ? 'Copied!' : 'Copy link'}
          >
            <i className={`fi fi-rr-${copied ? 'check' : 'copy'}`} />
          </button>

          {/* Open Original */}
          <a
            href={item.originalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="reader-btn"
            style={{ fontSize: '0.8rem' }}
            title="Open original website"
          >
            <span>Original</span>
            <i className="fi fi-rr-arrow-up-right" style={{ fontSize: '0.75rem' }} />
          </a>

          {/* Refresh / Re-extract */}
          {onRefreshItem && (
            <button
              className="reader-btn-icon"
              onClick={() => onRefreshItem(item)}
              title="Re-extract and refresh article"
              aria-label="Refresh article content"
            >
              <i className="fi fi-rr-refresh" />
            </button>
          )}

          {/* Delete item with 10s cancel timer */}
          <button
            className={`reader-btn-icon ${deleteSecondsLeft !== null ? 'is-timer-active' : ''}`}
            onClick={handleDeleteClick}
            title={deleteSecondsLeft !== null ? `Deleting in ${deleteSecondsLeft}s — Click to cancel` : 'Delete from library'}
            aria-label={deleteSecondsLeft !== null ? `Cancel deleting "${item.title}" (${deleteSecondsLeft}s remaining)` : 'Delete from library'}
            style={{
              color: '#ef4444',
              position: 'relative',
              overflow: 'visible',
              width: deleteSecondsLeft !== null ? 'auto' : undefined,
              padding: deleteSecondsLeft !== null ? '0 8px' : undefined,
              gap: deleteSecondsLeft !== null ? '4px' : undefined,
            }}
          >
            {deleteSecondsLeft !== null && (
              <svg className="reader-btn-timer-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                <rect className="reader-btn-timer-track" x="1" y="1" width="98" height="98" rx="4" />
                <rect
                  className="reader-btn-timer-indicator"
                  x="1" y="1" width="98" height="98" rx="4"
                  pathLength="100"
                />
              </svg>
            )}
            <i className={deleteSecondsLeft !== null ? 'fi fi-rr-cross-small' : 'fi fi-rr-trash'} />
            {deleteSecondsLeft !== null && (
              <span className="reader-btn-timer-text" style={{ fontSize: '0.72rem', fontWeight: 700 }}>
                {deleteSecondsLeft}s
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Scrollable Reader Document */}
      <div className="reader-detail-scroll">
        <article className="reader-article-container">
          <header className="reader-article-header">
            <div className="reader-article-meta-line">
              <span style={{ fontWeight: 600 }}>{item.sourceDomain || 'Web'}</span>
              <span>•</span>
              <span style={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.04em' }}>
                {item.sourceType || 'article'}
              </span>
              <span>•</span>
              <span>{calculateReadingTime(item.extractedText || item.description)}</span>
              {item.publishedAt && (
                <>
                  <span>•</span>
                  <span>{new Date(item.publishedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}</span>
                </>
              )}
            </div>

            <h1 className="reader-article-title">{item.title}</h1>

            <div className="reader-article-byline">
              {item.author && <span>By <strong>{item.author}</strong></span>}
              {item.publisher && item.publisher !== item.author && (
                <span>Published in <em>{item.publisher}</em></span>
              )}
              <a href={item.originalUrl} target="_blank" rel="noopener noreferrer">
                Visit original source →
              </a>
            </div>

            {/* Collections Badges Row */}
            <div className="reader-article-collections-bar">
              <span className="reader-collections-bar-label">
                <i className="fi fi-rr-folder" style={{ color: 'var(--reader-accent)' }} />
                <span>Collections:</span>
              </span>
              {itemCollections.length === 0 ? (
                <span className="reader-no-collections-text">None (Inbox)</span>
              ) : (
                itemCollections.map((col) => (
                  <span
                    key={col._id}
                    className="reader-article-col-chip"
                    style={{
                      borderColor: col.color ? `${col.color}40` : undefined,
                      background: col.color ? `${col.color}15` : undefined,
                    }}
                  >
                    <span
                      className="reader-collection-dot"
                      style={{ backgroundColor: col.color || 'var(--reader-accent)' }}
                    />
                    <span>{col.name}</span>
                  </span>
                ))
              )}
              {onEdit && (
                <button
                  type="button"
                  className="reader-collections-edit-link-btn"
                  onClick={() => onEdit(item)}
                  title="Move to one or multiple collections"
                >
                  <i className="fi fi-rr-edit" style={{ fontSize: '0.7rem' }} />
                  <span>Move / Edit</span>
                </button>
              )}
            </div>
          </header>

          {/* Video / Embed Player */}
          {item.mediaUrl && (item.sourceType === 'video' || item.contentType === 'video') && (
            <div className="reader-media-embed">
              <iframe
                src={item.mediaUrl}
                title={item.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', aspectRatio: '16/9', border: 0 }}
              />
            </div>
          )}

          {/* Audio / Podcast Player */}
          {item.mediaUrl && (item.sourceType === 'podcast' || item.contentType === 'audio' || item.mediaUrl.includes('megaphone.fm') || item.mediaUrl.includes('spotify.com') || item.mediaUrl.includes('simplecast')) && (
            <div className="reader-audio-player-wrap">
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--reader-text)', marginBottom: '8px' }}>
                <i className="fi fi-rr-headphones" style={{ marginRight: '6px' }} />
                Listen to Podcast / Audio Episode
              </div>
              {item.mediaUrl.endsWith('.mp3') || item.mediaUrl.endsWith('.m4a') || item.mediaUrl.endsWith('.wav') || item.mediaUrl.endsWith('.ogg') ? (
                <audio controls src={item.mediaUrl} preload="metadata" style={{ width: '100%' }}>
                  Your browser does not support the audio element.
                </audio>
              ) : (
                <iframe
                  src={item.mediaUrl}
                  title={item.title}
                  style={{ width: '100%', height: '180px', border: 0, borderRadius: '6px' }}
                  scrolling="no"
                  allow="autoplay"
                />
              )}
            </div>
          )}

          {/* Article Body Content */}
          <div
            className={`reader-article-body ${fontSerif ? 'serif' : ''}`}
            dangerouslySetInnerHTML={{
              __html: (() => {
                const cleaned = cleanArticleBody(item.extractedText);
                return cleaned && cleaned.length > 50
                  ? cleaned
                  : item.description
                  ? `<p>${item.description}</p>`
                  : '<p style="color:var(--reader-text-muted);font-style:italic;">No extracted body text available for this link. You can read it directly at the original source above.</p>';
              })(),
            }}
          />

          {/* Tags Manager Section */}
          <div style={{ marginTop: '36px', paddingTop: '20px', borderTop: '1px solid var(--reader-border-subtle)' }}>
            <div className="reader-notes-title">
              <i className="fi fi-rr-label" />
              <span>Tags</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
              {item.tags &&
                item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="reader-item-tag"
                    style={{ fontSize: '0.8rem', padding: '3px 8px', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    #{tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}
                      title="Remove tag"
                    >
                      ×
                    </button>
                  </span>
                ))}
              <input
                type="text"
                placeholder="+ Add tag (Press Enter)"
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                style={{
                  height: '28px',
                  padding: '0 8px',
                  fontSize: '0.78rem',
                  border: '1px dashed var(--reader-border)',
                  borderRadius: '3px',
                  background: 'transparent',
                  color: 'var(--reader-text)',
                  outline: 'none',
                }}
              />
            </div>
          </div>

          {/* Personal Notes Editor Box */}
          <div className="reader-notes-box">
            <div className="reader-notes-title">
              <i className="fi fi-rr-edit" />
              <span>Personal Notes & Highlights</span>
              {isNoteSaving && (
                <span style={{ fontSize: '0.72rem', color: 'var(--reader-accent)', marginLeft: 'auto' }}>
                  Saving…
                </span>
              )}
            </div>
            <textarea
              className="reader-notes-textarea"
              placeholder="Jot down notes, summary, key takeaways, or follow-ups for this article..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onBlur={handleSaveNote}
            />
          </div>
        </article>
      </div>
    </main>
  );
}
